// Used in activity-builder.component.html

// Angular
import { KeyValue } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatSelectChange } from "@angular/material/select";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { FileUploader } from "ng2-file-upload";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { LanguageHelper } from "#helpers/language.helper";
import { CookieService } from "#services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
import { AssetId, EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, AssetAssociation, CustomActivity, Edition, Level, assetTypeIds } from "@carnegie-learning/cure-types";
import { CustomActivityService } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "custom-activity-form",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./custom-activity-form.component.html",
    styleUrls: [
        "./custom-activity-form.component.scss",
        "../../../../../styles/form-styles.scss",
        "../../../../../styles/shared-styles.scss",
    ],
    standalone: false
})
export class CustomActivityFormComponent
    implements OnInit, OnChanges, OnDestroy
{
    componentInit = false;
    exists = false;
    assetId: AssetId = "";
    showFileProgressBar = false;
    disableUploadBtn = true;
    uploadedFile: string | null = null;
    mediaUrl: string | null = null;
    mediaType: string | null = null;
    activityName = "Custom";

    @Input() isDistributor = false;
    @Input() userGUID = "";
    @Input() currentCustomActivityForm?: CustomActivity;
    @Input() activePrograms: AssetAssociation[] = [];
    @Input() activeEditions: Edition[] = [];
    @Input() subjectId: SubjectId = "";
    @Input() editionId: EditionId = "";
    @Input() selectedAssets?: Asset[];
    @Input() activityFormValidity = false;
    @Input() editingActivity = false;

    @Output() create = new EventEmitter<CustomActivity>();
    @Output() update = new EventEmitter<CustomActivity>();
    @Output() cancel = new EventEmitter<any>();

    @Output() setCustomActivityFormData = new EventEmitter<CustomActivity>();
    @Output() setActivityFormValidity = new EventEmitter<boolean>();
    @Output() setCurrentActivityForm = new EventEmitter<CustomActivity>();

    stimulusForm = this.fb.group({
        displayName: [
            "",
            [Validators.required, this.formHelper.noWhitespaceValidator],
        ],
        description: [
            "",
            [
                Validators.required,
                this.formHelper.noQuillEditorWhitespaceValidator,
            ],
        ],
        stimuliDirection: ["", []],
        url: ["", []],
        file: ["", []],
        responseTypeUpload: [""],
        responseTypeShortAnswer: [""],
        responseTypeNone: [""],
        responseType: ["", Validators.required],
        publishedBy: ["", Validators.required],
        visibility: ["self", Validators.required],
        locales: [["en_US"], Validators.required],
        audiences: [["TCH", "STU"], Validators.required],
        scorePossible: [
            "",
            [
                Validators.required,
                this.formHelper.zeroLead,
                this.formHelper.removeInputWhiteSpaces,
                this.formHelper.scorePossibleValidator,
            ],
        ],
        levelIds: [[], [Validators.required]],
        unit: ["", []],
        editionIds: ["", [Validators.required]],
        subjectId: ["", [Validators.required]],
        assetTypeId: [assetTypeIds.CUSTOM_ACT, [Validators.required]],
        isResource: [false, Validators.required],
        isAssignable: [true, Validators.required],
        isDeleted: [false, Validators.required],
        audit: [{ createdBy: "" }, Validators.required],

        // uploadFileButton: ({ disabled: true }),

        // The following form parameters can only be set by the distributor.
        // The API ignores these values for non-distributors.
        lesson: ["", []],
        spineID: ["", []],
        page: ["", []],
        bookActivityNumber: ["", []],
    });

    selectedSubjectId: SubjectId = "";
    stimulusUnits: string[] = [];
    stimulusLevels: Level[] = [];
    levelLabel = "Level";

    visibilityOpts: KeyValue<string, string>[] = [];
    maxFileSize = 64 * 1024 * 1024; // 64 MB
    incorrectFileType = false;
    maxFileSizeExceeded = false;

    apiURL = (<any>window).apiHost + "/api/customActivity/upload-file";
    authToken = this.cookieService.get("CL_TOKEN2");
    header = { name: "authorization", value: this.authToken };
    uploader = new FileUploader({
        maxFileSize: this.maxFileSize,
        url: this.apiURL,
        authToken: this.authToken,
        autoUpload: false,
        method: "POST",
    });
    public hasBaseDropZoneOver = false;

    constructor(
        private fb: UntypedFormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        public localStorage: LocalStorage,
        private cdr: ChangeDetectorRef,
        public languageHelper: LanguageHelper,
        private formHelper: FormHelper,
        private titleService: Title,
        private cookieService: CookieService,
        private customActivityService: CustomActivityService
    ) {
        this.uploader.onAfterAddingFile = (item) => {
            item.withCredentials = false;
            this.disableUploadBtn = true;
            this.maxFileSizeExceeded = false;
            this.incorrectFileType = false;
        };

        this.uploader.onSuccessItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            this.disableUploadBtn = true;
            const responseObj = JSON.parse(response);
            this.uploadedFile = responseObj.file;
            this.mediaUrl = responseObj.mediaUrl;
            this.showFileProgressBar = false;

            this.mediaType = this.getMediaTypeFromFile(responseObj.mediaUrl);

            this.clearSubmissionSet();
            // this.stimulusForm.value.file = this.uploadedFile;
            this.stimulusForm.patchValue({
                file: this.uploadedFile,
            });
            this.onChange();

            this.cdr.detectChanges();
        };

        this.uploader.onErrorItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            this.showFileProgressBar = false;
            this.cdr.detectChanges();
        };

        this.uploader.onWhenAddingFileFailed = (fileItem) => {
            const fileType = this.getMediaTypeFromFile(fileItem.name || "");
            if (fileItem.size > this.maxFileSize) {
                this.maxFileSizeExceeded = true;
            }
            if (!fileItem.type) {
                this.incorrectFileType = true;
            }
        };
    }

    ngOnInit() {
        this.stimulusForm.patchValue({ editionIds: [this.editionId] });
        this.levelLabel = this.languageHelper.gradeLevelReturn(this.subjectId);
        this.setActivityNameAndDocumentTitle();
        this.visibilityOpts = this.formHelper.getVisibilityOpts();
    }

    getMediaTypeFromFile(file: string) {
        if (!file) {
            return null;
        }
        if (
            file.includes(".jpg") ||
            file.includes(".jpeg") ||
            file.includes(".png") ||
            file.includes(".bmp") ||
            file.includes(".gif")
        ) {
            return "image";
        } else if (
            file.includes(".mov") ||
            file.includes(".mp4") ||
            file.includes(".webm")
        ) {
            return "video";
        } else {
            return "other";
        }
    }

    fileSelected() {
        if (this.uploader.queue.length > 0) {
            // If the Queue is greater than 1, remove the first one.
            if (this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }

            this.showFileProgressBar = false;
            this.disableUploadBtn = false;
        }
    }

    uploadFile() {
        this.uploader.queue[0].upload();
        this.showFileProgressBar = true;
    }

    clearSubmissionSet() {
        this.uploader.queue[0].remove();
        this.disableUploadBtn = true;
    }

    public onUploaderChange(event: any): void {
        event.target.value = "";
    }

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            this.currentCustomActivityForm &&
            this.currentCustomActivityForm.assetId
        ) {
            if (this.currentCustomActivityForm.subjectId) {
                this.subjectId = this.currentCustomActivityForm.subjectId;
            }
            this.setActivityNameAndDocumentTitle();
            this.exists = true;
            this.assetId = this.currentCustomActivityForm.assetId;

            // when editing, to populate the form, must specify format of the form data!
            // api gives flat obj but form requires nested obj
            const responseTypeUpload =
                this.currentCustomActivityForm.responseType === "both" ||
                this.currentCustomActivityForm.responseType === "file";
            const responseTypeShortAnswer =
                this.currentCustomActivityForm.responseType === "both" ||
                this.currentCustomActivityForm.responseType === "text";
            const responseTypeNone =
                this.currentCustomActivityForm.responseType === "none";

            let publishedBy = this.currentCustomActivityForm.publishedBy;
            if (publishedBy == null && this.isDistributor && this.userGUID) {
                // Fix for legacy tasks that don't have an ownerID set.
                publishedBy = this.userGUID;
            }

            this.stimulusForm.patchValue({
                publishedBy,
                displayName: this.currentCustomActivityForm.displayName,
                description: this.currentCustomActivityForm.description,
                stimuliDirection:
                    this.currentCustomActivityForm.stimuliDirection,
                url: this.currentCustomActivityForm.url,
                scorePossible: this.currentCustomActivityForm.scorePossible,
                responseType: this.currentCustomActivityForm.responseType,
                responseTypeUpload: responseTypeUpload,
                responseTypeShortAnswer: responseTypeShortAnswer,
                responseTypeNone: responseTypeNone,
                visibility: this.currentCustomActivityForm.visibility,
                audiences: this.currentCustomActivityForm.audiences,
                unit: this.currentCustomActivityForm.unit,
                levelIds: this.currentCustomActivityForm.levelIds + "",
                editionIds: this.currentCustomActivityForm.editionIds,
                subjectId: this.currentCustomActivityForm.subjectId,
                audit: this.currentCustomActivityForm.audit,
                assetTypeId: this.currentCustomActivityForm.assetTypeId,

                // Distributor only
                lesson: this.currentCustomActivityForm.lesson,
                page: this.currentCustomActivityForm.page,
                bookId: this.currentCustomActivityForm.bookId,
                bookActivityNumber:
                    this.currentCustomActivityForm.bookActivityNumber,
            });

            if (this.currentCustomActivityForm.file) {
                if (!this.mediaUrl) {
                    this.customActivityService
                        .getFileUrl(this.currentCustomActivityForm)
                        .subscribe((path) => {
                            this.mediaUrl = path;
                            this.cdr.detectChanges();
                        });
                }

                /** discrepancy in logic for `file` property
                 *  when submitting form, the variable is resource path (eg. "/uploads/1111/file.png")
                 *  but when retrieving object for Stimuli from API response the value is actually the mediaURL
                 *  (eg. "https://p-content-passport.emcl.com/uploads/1111/file.png?someRandomKeys").
                 *  To correct this, the url has to get sanitized when in edit mode for stimulus form.
                 **/
                this.uploadedFile = this.sanitizeFileURLToResourcePath(
                    this.currentCustomActivityForm.file
                );

                this.mediaType = this.getMediaTypeFromFile(
                    this.currentCustomActivityForm.file
                );
            }
            if (this.currentCustomActivityForm.subjectId) {
                this.selectedSubjectId =
                    this.currentCustomActivityForm.subjectId;
            }
        } else {
            this.stimulusForm.patchValue({
                publishedBy: this.userGUID,
                subjectId: this.subjectId,
                audit: {
                    createdBy: this.userGUID,
                },
            });
        }

        this.setValidUnitsAndLevelsForSubjectId(this.selectedSubjectId);
    }

    sanitizeFileURLToResourcePath(fileURL: string) {
        if (fileURL === null) {
            return null;
        } else if (fileURL.includes("-content.emcl.com")) {
            /**
             *  URL needs to get sanitized to correct format
             *  eg. https://p-content.emcl.com/uploads/stimulus/11111/2349-gmp2_4.jpg?Expires=1511111111162&Signature=SomeSignature&Key-Pair-Id=SOMEID
             *  returns: uploads/stimulus/11111/2349-gmp2_4.jpg
             */
            const startIndex = 27;
            const endIndex = fileURL.indexOf("?Expires");
            return fileURL.slice(startIndex, endIndex);
        } else {
            // already sanitized
            return fileURL;
        }
    }

    // Distributor ONLY
    onSubjectChange(event: MatSelectChange) {
        this.selectedSubjectId = event.value;
        this.setValidUnitsAndLevelsForSubjectId(event.value);
        this.stimulusForm.patchValue({
            subjectId: event.value,
            unit: null,
            level: null,
        });
    }

    setValidUnitsAndLevelsForSubjectId(subjectId = ""): void {
        const sId: SubjectId = subjectId || this.subjectId;
        this.formHelper.setSubjectAndEdition(
            sId,
            this.editionId,
            this.activePrograms,
            this.activeEditions
        );
        // levels
        this.stimulusLevels = this.formHelper.getFormLevels();
        if (
            this.editingActivity &&
            this.currentCustomActivityForm?.editionIds
        ) {
            // Here is to limit the level for cloned or editing existed asset. It should not run while creating a new asset
            this.stimulusLevels = [];
            this.currentCustomActivityForm?.editionIds.forEach((e) => {
                this.formHelper.setSubjectAndEdition(
                    sId,
                    e,
                    this.activePrograms,
                    this.activeEditions
                );
                const levels = this.formHelper.getFormLevelsForAssetsLevelIds(
                    this.currentCustomActivityForm?.levelIds
                );
                Array.prototype.push.apply(this.stimulusLevels, levels);
            });
        }
        // units
        this.stimulusUnits = this.formHelper.getFormUnits();
    }

    sortLevels(
        a: KeyValue<number, string>,
        b: KeyValue<number, string>
    ): number {
        return a.value.localeCompare(b.value);
    }

    scoreRangeError(field: string) {
        return (
            this.stimulusForm.get(field)?.hasError("pattern") &&
            this.stimulusForm.get(field)?.touched
        );
    }

    submit() {
        if (this.stimulusForm.valid) {
            this.stimulusForm.value.file = this.uploadedFile;
            if (!this.exists) {
                this.create.emit(this.stimulusForm.value);
            } else {
                this.stimulusForm.value.assetId = this.assetId;
                this.update.emit(this.stimulusForm.value);
            }

            this.stimulusForm.markAsPristine();
        }
    }

    cancelStimulusTask() {
        this.cancel.emit();
    }

    setDirections(editor: any) {
        // TODO: Just remove? Appears not to be called anywhere, ever?
        // Force form to recalculate.
        this.stimulusForm.updateValueAndValidity();
    }

    responseTypeButtonChange(event: MatCheckboxChange, type: string) {
        if (type === "none") {
            this.stimulusForm.patchValue({
                responseTypeShortAnswer: (event.checked = false),
                responseTypeUpload: (event.checked = false),
            });
        } else {
            this.stimulusForm.patchValue({
                responseTypeNone: (event.checked = false),
            });
        }
        this.convertResponseTypeValue();
        this.onChange();
    }

    convertResponseTypeValue() {
        let value = "";
        if (
            this.stimulusForm.value.responseTypeShortAnswer &&
            this.stimulusForm.value.responseTypeUpload
        ) {
            value = "both";
        } else if (this.stimulusForm.value.responseTypeShortAnswer) {
            value = "text";
        } else if (this.stimulusForm.value.responseTypeUpload) {
            value = "file";
        } else if (this.stimulusForm.value.responseTypeNone) {
            value = "none";
        }

        this.stimulusForm.patchValue({ responseType: value });
        this.stimulusForm.updateValueAndValidity();
    }

    setActivityNameAndDocumentTitle() {
        if (this.exists) {
            this.titleService.setTitle(
                "Carnegie Learning - Edit Custom Activity"
            );
        } else {
            this.titleService.setTitle(
                "Carnegie Learning - Create Custom Activity"
            );
        }
    }

    ifMathProgram() {
        if (
            this.subjectId &&
            this.languageHelper.ifMathLanguage(this.subjectId)
        ) {
            return true;
        }
    }

    onChange() {
        const formValidity = this.stimulusForm.valid;
        if (!this.componentInit && this.editingActivity) {
            this.componentInit = true;
        }
        if (this.stimulusForm) {
            this.setActivityFormValidity.emit(formValidity);
            if (this.exists) {
                this.stimulusForm.value.assetId = this.assetId;
            }

            this.setCustomActivityFormData.emit(this.stimulusForm.value);
        }
    }

    setQuillEditorTouched() {
        this.stimulusForm.controls.description.markAsTouched();
    }

    ngOnDestroy(): void {
        this.setCurrentActivityForm.emit(this.stimulusForm.value);
    }
}
